import { makeStyles } from '@mui/styles';
import Loader from 'apollo-react/components/Loader';
import Modal from 'apollo-react/components/Modal';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ImportInfo from './ImportInfo';
import UploadPowerBIFile from './UploadPowerBIFile';
import {
  clearCurrentImportRecordData,
  initiatePowerBIImport,
  initiatePowerBIImportProcess,
  setPowerBiImportModalOpen,
} from '../../actions/PowerBIActions';
import { IMPORT_STEPS, STEPS_DESCRIPTION } from '../../constants/PowerBIConstants';
import { useTranslation } from '../../custom-hooks/useTranslation';
import {
  getCurrentRecordId,
  getImportRecordProp,
  getIsPowerBIImportModalOpen,
} from '../../selectors/PowerBISelectors';
import { StepProgress } from '../StepProgress';

const useStyles = makeStyles({
  modal: {
    minWidth: '50%',
  },
});

const ImportReportModal = () => {
  const classes = useStyles();
  const translateString = useTranslation();
  const dispatch = useDispatch();

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [buttonList, setButtonList] = useState([
    {
      label: translateString('Cancel'),
      'ui-ta': 'PowerBICancelImport',
    },
    {
      label: translateString('Import'),
      'ui-ta': 'PowerBIStartImport',
    },
  ]);

  const isModalOpen = useSelector(getIsPowerBIImportModalOpen);
  const importStep = useSelector(getImportRecordProp('importStep'));
  const hasImportError = useSelector(getImportRecordProp('hasImportError'));
  const isLoading = useSelector(getImportRecordProp('isLoading'));
  const currentRecordId = useSelector(getCurrentRecordId);

  const handleImportClick = async () => {
    await dispatch(initiatePowerBIImport(selectedFiles[0], currentRecordId));
  };

  const handleModalClose = () => {
    if (importStep < IMPORT_STEPS.START_IMPORT) {
      dispatch(clearCurrentImportRecordData());
    }

    dispatch(setPowerBiImportModalOpen(false));
  };

  const cancelBtnProps = {
    label: translateString('Cancel'),
    'ui-ta': 'PowerBICancelImport',
    disabled: isLoading,
    onClick: handleModalClose,
  };

  const startNewImport = () => {
    setSelectedFiles([]);
    dispatch(clearCurrentImportRecordData());
  };

  const startNewImportBtnPops = {
    label: translateString('Start New Import'),
    onClick: startNewImport,
    'ui-ta': 'MasterSetupImportModal-StartNewImportButton',
  };

  useEffect(() => {
    if (!importStep) {
      dispatch(initiatePowerBIImportProcess());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasImportError) {
      setButtonList([cancelBtnProps, startNewImportBtnPops]);
    } else if (importStep < IMPORT_STEPS.UPLOAD_COMPLETED) {
      setButtonList([
        cancelBtnProps,
        {
          label: translateString('Import'),
          onClick: handleImportClick,
          disabled: !selectedFiles.length || isLoading,
          'ui-ta': 'MasterSetupImportModal-ImportButton',
        },
      ]);
    } else if (importStep === IMPORT_STEPS.UPLOAD_COMPLETED) {
      setButtonList([cancelBtnProps, startNewImportBtnPops]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importStep, isLoading, selectedFiles.length]);

  const showFileUpload = !isLoading && importStep < IMPORT_STEPS.START_IMPORT;
  const showImportInfo = importStep === IMPORT_STEPS.UPLOAD_COMPLETED || hasImportError;

  return (
    <Modal
      open={isModalOpen}
      variant="default"
      style={{ zIndex: 3003 }}
      onClose={handleModalClose}
      title={translateString('Import')}
      className={classes.modal}
      disableBackdropClick
      disableEscapeKeyDown
      buttonProps={buttonList}
    >
      <StepProgress activeStep={importStep} steps={STEPS_DESCRIPTION} hasError={hasImportError} />
      {isLoading && (
        <div style={{ position: 'relative', height: 135 }}>
          <Loader isInner />
        </div>
      )}
      {showFileUpload && (
        <UploadPowerBIFile selectedFiles={selectedFiles} setSelectedFiles={setSelectedFiles} />
      )}
      {showImportInfo && <ImportInfo />}
    </Modal>
  );
};

export default ImportReportModal;
